/**
 * Pure geometry helpers for the RegionSelector component (src/components/ui/RegionSelector.tsx).
 *
 * Kept dependency-free (no DOM, no React) so they typecheck and unit-test independently of the
 * component's pointer-event wiring. All coordinates are normalized 0..1 within the image, matching
 * `BoxCoordinates` in src/types/index.ts.
 */
import type { BoxCoordinates } from '@/types';

/** Minimum normalized box width/height — anything smaller is treated as an accidental tap and discarded. */
export const MIN_REGION_SIZE = 0.03;

export type RegionCorner = 'tl' | 'tr' | 'bl' | 'br';

export interface NormalizedPoint {
  x: number;
  y: number;
}

/** Subset of DOMRect needed for coordinate conversion — lets callers pass a plain object in tests. */
export interface OverlayRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Clamp a value to the [0, 1] range. */
export function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** Convert a pointer-event client position to a point normalized 0..1 within `rect`, clamped to bounds. */
export function clientToNormalizedPoint(
  clientX: number,
  clientY: number,
  rect: OverlayRect
): NormalizedPoint {
  if (rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
  return {
    x: clamp01((clientX - rect.left) / rect.width),
    y: clamp01((clientY - rect.top) / rect.height),
  };
}

/**
 * Re-order box corners so x1<=x2 and y1<=y2. Consumers like `boxToMaskPng` already min/max
 * internally, but emitting a normalized box to callers (onBoxChange) is cleaner and avoids
 * surprises for any future consumer that assumes top-left/bottom-right ordering.
 */
export function normalizeBox(box: BoxCoordinates): BoxCoordinates {
  return {
    x1: Math.min(box.x1, box.x2),
    y1: Math.min(box.y1, box.y2),
    x2: Math.max(box.x1, box.x2),
    y2: Math.max(box.y1, box.y2),
  };
}

export function boxWidth(box: BoxCoordinates): number {
  return Math.abs(box.x2 - box.x1);
}

export function boxHeight(box: BoxCoordinates): number {
  return Math.abs(box.y2 - box.y1);
}

/** True when a drawn box is smaller than the minimum useful size (an accidental tap, not a deliberate selection). */
export function isBoxTooSmall(box: BoxCoordinates, minSize: number = MIN_REGION_SIZE): boolean {
  return boxWidth(box) < minSize || boxHeight(box) < minSize;
}

/**
 * Apply a new corner position during a resize-handle drag. The opposite corner is derived from the
 * box's current normalized extent, so dragging a handle past the opposite corner flips which corner
 * is "active" for the next move event — mirrors Canva/Photoshop-style corner handles.
 */
export function applyCornerDrag(
  box: BoxCoordinates,
  corner: RegionCorner,
  point: NormalizedPoint
): BoxCoordinates {
  const { x1, y1, x2, y2 } = normalizeBox(box);
  switch (corner) {
    case 'tl':
      return { x1: point.x, y1: point.y, x2, y2 };
    case 'tr':
      return { x1, y1: point.y, x2: point.x, y2 };
    case 'bl':
      return { x1: point.x, y1, x2, y2: point.y };
    case 'br':
      return { x1, y1, x2: point.x, y2: point.y };
  }
}

/** Translate a box by (dx, dy), clamping so it stays fully within [0,1] on both axes. Size is preserved. */
export function translateBox(box: BoxCoordinates, dx: number, dy: number): BoxCoordinates {
  const { x1, y1, x2, y2 } = normalizeBox(box);
  const width = x2 - x1;
  const height = y2 - y1;
  const nx1 = Math.min(clamp01(x1 + dx), 1 - width);
  const ny1 = Math.min(clamp01(y1 + dy), 1 - height);
  return { x1: nx1, y1: ny1, x2: nx1 + width, y2: ny1 + height };
}
